import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown, ChevronRight } from 'lucide-react';
import { usePageGuideStore, type ActiveGuide } from '../../stores/pageGuideStore';
import {
  REPLAY_TARGETS, guideTarget, nextChapter, phoneDock, placeCard, ringRect, spotlightRect, stepSections,
  type CardPlacement, type GuideStep, type PageGuide, type Rect,
} from '../../utils/pageGuide';
import { useUserStore } from '../../stores/userStore';
import { otherGuidanceOpen } from '../../hooks/usePageGuide';
import { GuideMenu } from './PageGuideButton';
import { guideChoice } from './guideChoice';

const PHONE = '(max-width: 639px)';
/** Near-black, no blue cast (owner, 2026-10-10). */
const DIM = 'rgba(0, 0, 0, 0.72)';
/** The brand gradient — the app's own variable, with the brand colours as a fallback. */
const RING = 'var(--primary-gradient, linear-gradient(135deg, #3CC2DD, #A739E2, #F58C56))';
const MISSING_MS = 500;
/** A phone docks its card once the target has stopped moving (a step's scroll). */
const SETTLE_MS = 200;
const sameRect = (a: Rect | null, b: Rect | null) =>
  a === b || (!!a && !!b && Math.abs(a.left - b.left) < 0.5 && Math.abs(a.top - b.top) < 0.5 && Math.abs(a.width - b.width) < 0.5 && Math.abs(a.height - b.height) < 0.5);
const media = (q: string) => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(q).matches : false);
const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
const typing = (el: EventTarget | null) => {
  const t = el as HTMLElement | null;
  return !!t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable);
};
/**
 * ⭐ A `display: contents` target has no box of its own (2026-10-16): it stands for
 * what it holds — an area page's centre, whatever the area puts there, anchored
 * without changing its layout. It is measured as the box round its shown children,
 * and scrolled to by its first one.
 */
const isContents = (el: HTMLElement) => window.getComputedStyle(el).display === 'contents';
const shownChildren = (el: HTMLElement) => Array.from(el.children).filter(c => c.getClientRects().length > 0) as HTMLElement[];
const measure = (el: HTMLElement | null): Rect | null => {
  if (!el) return null;
  if (isContents(el)) {
    const rs = shownChildren(el).map(c => c.getBoundingClientRect());
    if (!rs.length) return null;
    const left = Math.min(...rs.map(r => r.left));
    const top = Math.min(...rs.map(r => r.top));
    return { left, top, width: Math.max(...rs.map(r => r.right)) - left, height: Math.max(...rs.map(r => r.bottom)) - top };
  }
  const r = el.getBoundingClientRect();
  return { left: r.left, top: r.top, width: r.width, height: r.height };
};
/** Wholly inside the viewport — then there is nothing to scroll. */
const inView = (el: HTMLElement) => {
  const r = measure(el);
  return !!r && r.top >= 0 && r.left >= 0 && r.top + r.height <= window.innerHeight && r.left + r.width <= window.innerWidth;
};
/** The first of these targets that is actually on screen. */
const findTarget = (ids: readonly string[]) => {
  for (const id of ids) {
    const el = document.querySelector<HTMLElement>(guideTarget(id));
    if (el && (isContents(el) ? shownChildren(el).length > 0 : el.getClientRects().length > 0)) return el;
  }
  return null;
};

/**
 * ⭐ The page-guide layer (owner, 2026-10-10). Mounted ONCE at the app root. While a
 * guide runs it dims the app (near-black), rings the step's target in the brand
 * gradient and shows a card beside it (docked on a phone: at the bottom, or the top
 * when that covers less of the target). Clicks
 * outside the card and the spotlighted thing are cancelled (a capture listener, not
 * blocking panels — so the page still scrolls).
 *
 * ⚠️ An ACTION step moves on when its target is clicked, or has a value changed
 *    (a slider) — nothing else does.
 * ⚠️ A missing target: an OPTIONAL step is skipped; any other shows a centred card
 *    with the whole app dimmed — never a crash or a card pointing at nothing.
 * ⚠️ Esc and "Skip guide" finish it (counted as seen). Focus stays in the card
 *    (and the target, on an action step). Reduced motion = no animation.
 * ⭐ Then the closing POINTER: dimmed, pointing at Guide (the phone's menu button),
 *    "Replay it any time from here" — after the overview's Done always; after a
 *    chapter's Done or any Skip until seen once.
 * ⭐ A guide with SECTIONS (an end-to-end walk, 2026-10-11) gets a Steps button: the
 *    whole list, grouped, finished steps ticked — jump to any of them. Past 8 steps
 *    the dots become a bar.
 * Also owns the Shift+? shortcut — which offers the SAME choice as Guide.
 */
export function PageGuideOverlay() {
  const active = usePageGuideStore(s => s.active);
  const pointer = usePageGuideStore(s => s.pointer);
  const stack = usePageGuideStore(s => s.stack);
  const seen = useUserStore(s => s.pageGuidesSeen);
  // The choice Shift+? opened — null while closed.
  const [choice, setChoice] = useState<ReturnType<typeof guideChoice> | null>(null);
  const closeChoice = useCallback(() => setChoice(null), []);
  // A page that comes or goes takes its choice with it.
  useEffect(() => { setChoice(null); }, [stack]);

  // Shift+? — the page's guide, from anywhere but a text field or over a dialog.
  // ⭐ With more than one way in (the overview and a chapter, or "From <section>") it
  // offers the same choice Guide does (owner, 2026-10-16) — before, it always took
  // the DEEPEST guide, so on Home it could never reach Home's own overview. With one
  // way in it starts straight away, as it always did. Pressed again, it closes.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '?' || typing(e.target) || e.metaKey || e.ctrlKey || e.altKey) return;
      const s = usePageGuideStore.getState();
      if (s.active || !s.registered || otherGuidanceOpen()) return;
      e.preventDefault();
      const offered = guideChoice(s.stack);
      if (offered.entries.length > 1) setChoice(c => (c ? null : offered));
      else s.start();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  if (active) return createPortal(<GuideStepLayer key={`${active.guide.id}:${active.step}`} active={active} />, document.body);
  if (pointer) return createPortal(<PointerLayer />, document.body);
  if (choice) {
    // Beside the rail's Guide button when it is on screen; centred when it is not.
    const button = document.querySelector<HTMLElement>(guideTarget('nav.guide'));
    return (
      <GuideMenu anchor={button && button.getClientRects().length ? button : null} anchorToggles={false} {...choice} seen={seen}
        onClose={closeChoice} onPick={entry => { setChoice(null); usePageGuideStore.getState().start(entry.guide, entry.step); }} />
    );
  }
  return null;
}

function GuideStepLayer({ active }: { active: ActiveGuide }) {
  const { guide, step: index } = active;
  const step = guide.steps[index];
  const total = guide.steps.length;
  const last = index === total - 1;
  const sectioned = guide.steps.some(s => s.section);
  const [listOpen, setListOpen] = useState(false);
  const act = step.advanceOn;
  const store = usePageGuideStore.getState;
  // Done hands straight on to the open tab's unseen chapter — then it says so.
  const chains = usePageGuideStore(s => last && !!nextChapter(s.stack, guide, useUserStore.getState().pageGuidesSeen));
  const reduced = media('(prefers-reduced-motion: reduce)');
  const btn = 'min-h-[44px] sm:min-h-[32px] px-3 rounded-lg text-xs font-semibold';
  // Stable handlers: Spotlight's listeners/timers re-arm when these change.
  const onAction = useCallback(() => store().next(), [store]);
  const onMissing = useCallback(() => store().skipMissing(), [store]);
  const onEscape = useCallback(() => store().finish('skip'), [store]);
  return (
    <Spotlight
      targets={step.target ? [step.target] : []}
      title={step.title}
      body={step.body}
      items={step.items}
      missingBody={step.missingBody}
      list={listOpen ? <StepsList guide={guide} current={index} done={active.done} onJump={i => store().jump(i)} /> : undefined}
      action={act}
      onAction={onAction}
      onMissing={step.optional ? onMissing : undefined}
      onEscape={onEscape}
      // A one-step chapter needs no "Step 1 of 1".
      progress={total > 1 && (
        <>
          {total > 8 ? (
            <span className="h-1 w-14 shrink-0 overflow-hidden rounded-full bg-muted" aria-hidden="true">
              <span className="block h-full rounded-full bg-foreground" style={{ width: `${((index + 1) / total) * 100}%` }} />
            </span>
          ) : (
            <span className="flex gap-1" aria-hidden="true">
              {guide.steps.map((s, i) => <span key={s.id} className={`h-1.5 w-1.5 rounded-full ${i <= index ? 'bg-foreground' : 'bg-muted'}`} />)}
            </span>
          )}
          <span aria-live="polite" className="min-w-0 truncate">Step {index + 1} of {total}{step.section ? ` · ${step.section}` : ''}</span>
          {sectioned && (
            <button type="button" onClick={() => setListOpen(o => !o)} aria-expanded={listOpen}
              className="ml-auto inline-flex min-h-[32px] shrink-0 items-center gap-0.5 rounded-md px-1.5 font-semibold text-foreground hover:bg-muted sm:min-h-[24px]">
              Steps <ChevronDown className={`h-3 w-3 transition-transform ${listOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
            </button>
          )}
        </>
      )}
      footer={(
        <>
          {act ? (
            <span className="flex-1 min-w-0 inline-flex items-center gap-1.5 text-xs font-semibold text-sky-500">
              <span className={`h-2 w-2 rounded-full bg-sky-500 ${reduced ? '' : 'animate-pulse'}`} aria-hidden="true" />
              {step.waitingText ?? 'Waiting for you'}
            </span>
          ) : <span className="flex-1" />}
          <button type="button" onClick={() => store().finish('skip')} className={`${btn} text-sky-500 hover:underline`}>Skip guide</button>
          {!act && index > 0 && (
            <button type="button" onClick={() => store().back()} className={`${btn} border border-border hover:bg-muted`}>Back</button>
          )}
          {!act && (
            <button type="button" data-guide-primary="" onClick={() => store().next()} className={`${btn} bg-foreground text-background`}>
              {last ? (chains ? 'Continue' : 'Done') : 'Next'}
            </button>
          )}
        </>
      )}
    />
  );
}

/**
 * ⭐ The Steps list (2026-10-11): every step under its section, finished ones ticked,
 * the current one marked — pick one to go straight there (the page opens what it
 * talks about first).
 */
function StepsList({ guide, current, done, onJump }: { guide: PageGuide; current: number; done: number[]; onJump: (i: number) => void }) {
  return (
    <nav aria-label="Steps" className="mt-2 max-h-[45vh] overflow-y-auto rounded-lg border border-border">
      {stepSections(guide).map(sec => (
        <div key={sec.steps[0].step.id} className="border-t border-border px-2 py-1.5 first:border-t-0">
          {sec.section && <p className="px-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{sec.section}</p>}
          <ol>
            {sec.steps.map(({ index, step }) => {
              const isDone = done.includes(index);
              const here = index === current;
              return (
                <li key={step.id}>
                  <button type="button" onClick={() => onJump(index)} aria-current={here ? 'step' : undefined}
                    aria-label={`Step ${index + 1}: ${step.title}${isDone ? ' (done)' : ''}`}
                    className={`flex w-full items-center gap-2 rounded-md px-1 py-1 text-left text-xs transition-colors hover:bg-muted ${here ? 'bg-muted' : ''}`}>
                    <span aria-hidden="true" className="flex w-3.5 shrink-0 justify-center">
                      {isDone ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : here ? <ChevronRight className="h-3.5 w-3.5 text-foreground" /> : null}
                    </span>
                    <span aria-hidden="true" className="w-4 shrink-0 tabular-nums text-muted-foreground">{index + 1}</span>
                    <span aria-hidden="true" className={`min-w-0 truncate ${here ? 'font-semibold text-foreground' : 'text-foreground'}`}>{step.title}</span>
                  </button>
                </li>
              );
            })}
          </ol>
        </div>
      ))}
    </nav>
  );
}

/** "Replay this guide any time from here" — dimmed, pointing at Guide (or the phone's menu). */
function PointerLayer() {
  const dismiss = usePageGuideStore(s => s.dismissPointer);
  const phone = media(PHONE);
  return (
    <Spotlight
      targets={REPLAY_TARGETS}
      title="Replay any time"
      body={phone
        ? 'Open the menu and choose “Guide for this page” whenever you want this guide again.'
        : 'Press Guide whenever you want this page’s guide again — or press Shift + ?.'}
      onEscape={dismiss}
      footer={(
        <>
          <span className="flex-1" />
          <button type="button" data-guide-primary="" onClick={dismiss} className="min-h-[44px] sm:min-h-[32px] px-3 rounded-lg text-xs font-semibold bg-foreground text-background">Got it</button>
        </>
      )}
    />
  );
}

/**
 * One spotlit moment: the dim with its hole, the brand ring, the card beside the
 * target, the click guard, focus, Esc. Shared by guide steps and the pointer.
 */
function Spotlight({ targets, title, body, missingBody, list, items, progress, footer, action, onAction, onMissing, onEscape }: {
  targets: readonly string[];
  title: string;
  body: string;
  /** Said instead of `body` (and its items) while the target is not on the page. */
  missingBody?: string;
  /** The Steps list, shown under the body while open. */
  list?: React.ReactNode;
  items?: GuideStep['items'];
  progress?: React.ReactNode;
  footer: React.ReactNode;
  action?: 'target-click' | 'target-change';
  onAction?: () => void;
  onMissing?: () => void;
  onEscape: () => void;
}) {
  const titleId = useId();
  const bodyId = useId();
  const cardRef = useRef<HTMLDivElement>(null);
  const elRef = useRef<HTMLElement | null>(null);
  const [view, setView] = useState({ width: window.innerWidth, height: window.innerHeight });
  // ⚠️ Measured BEFORE the first paint: starting from null painted one frame with the
  // whole app dimmed and no hole, then cut the hole and moved the card — the "twitch"
  // the owner saw between steps and between guides.
  const [target, setTarget] = useState<Rect | null>(() => measure(findTarget(targets)));
  const [pos, setPos] = useState<CardPlacement | null>(null);
  const [phone, setPhone] = useState(media(PHONE));
  const [dock, setDock] = useState<'top' | 'bottom'>('bottom');
  const reduced = media('(prefers-reduced-motion: reduce)');
  const key = targets.join('|');

  // Bring the target into view once (only if it is not already), then follow it.
  // A layout effect, so the first measurement lands before the browser paints.
  useLayoutEffect(() => {
    let raf = 0;
    let scrolled = false;
    const tick = () => {
      elRef.current = findTarget(key ? key.split('|') : []);
      if (elRef.current && !scrolled) {
        scrolled = true;
        if (!inView(elRef.current)) {
          const box = isContents(elRef.current) ? shownChildren(elRef.current)[0] : elRef.current;
          box?.scrollIntoView?.({ block: media(PHONE) ? 'center' : 'nearest', inline: 'nearest', behavior: reduced ? 'auto' : 'smooth' });
        }
      }
      const next = measure(elRef.current);
      setTarget(prev => (sameRect(prev, next) ? prev : next));
      setView(v => (v.width === window.innerWidth && v.height === window.innerHeight ? v : { width: window.innerWidth, height: window.innerHeight }));
      setPhone(p => (p === media(PHONE) ? p : media(PHONE)));
      raf = window.requestAnimationFrame(tick);
    };
    tick();
    return () => window.cancelAnimationFrame(raf);
  }, [key, reduced]);

  // An optional step whose target never shows up is skipped.
  useEffect(() => {
    if (!onMissing) return;
    const t = window.setTimeout(() => { if (!elRef.current) onMissing(); }, MISSING_MS);
    return () => window.clearTimeout(t);
  }, [onMissing]);

  const spot = spotlightRect(target, view);
  const ring = spot && ringRect(spot, view);

  // Place the card beside the spotlight (desktop) once its size is known.
  // ⭐ A phone DOCKS it instead — at the bottom, or the top when that covers less of
  // the target (`phoneDock`, 2026-10-16). Decided once the target has settled, so a
  // step's scroll does not bounce the card between the two.
  useLayoutEffect(() => {
    const card = cardRef.current;
    if (!card) { setPos(null); return; }
    if (phone) {
      setPos(null);
      const t = window.setTimeout(() => {
        const next = phoneDock(spot, card.offsetHeight, view);
        setDock(d => (d === next ? d : next));
      }, SETTLE_MS);
      return () => window.clearTimeout(t);
    }
    const next = placeCard(spot, { width: card.offsetWidth, height: card.offsetHeight }, view);
    setPos(p => (p && p.left === next.left && p.top === next.top && p.side === next.side ? p : next));
  }, [spot?.left, spot?.top, spot?.width, spot?.height, view.width, view.height, phone, !!list, title, body]); // eslint-disable-line react-hooks/exhaustive-deps

  // An action step moves on when the person clicks the target / changes a value in it.
  useEffect(() => {
    if (!action || !onAction) return;
    const type = action === 'target-click' ? 'click' : 'change';
    const delay = action === 'target-click' ? 180 : 600;
    let fired = false;
    const onEvent = (e: Event) => {
      const el = elRef.current;
      if (fired || !el || !(e.target instanceof Node) || !el.contains(e.target)) return;
      fired = true;
      window.setTimeout(onAction, delay);
    };
    document.addEventListener(type, onEvent, true);
    return () => document.removeEventListener(type, onEvent, true);
  }, [action, onAction]);

  // Focus into the card; Tab stays in the card (+ the target on an action step); Esc.
  useEffect(() => {
    const card = cardRef.current;
    const primary = card?.querySelector<HTMLElement>('[data-guide-primary]');
    if (primary) primary.focus(); else card?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onEscape(); return; }
      if (e.key !== 'Tab' || !card) return;
      const pool = [
        ...Array.from(card.querySelectorAll<HTMLElement>(FOCUSABLE)),
        ...(action && elRef.current ? Array.from(elRef.current.querySelectorAll<HTMLElement>(FOCUSABLE)) : []),
      ];
      if (!pool.length) return;
      const i = pool.indexOf(document.activeElement as HTMLElement);
      e.preventDefault();
      pool[(i + (e.shiftKey ? -1 : 1) + pool.length) % pool.length].focus();
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [action, onEscape]);

  // Everything outside the card and the spotlighted thing is dimmed AND not clickable.
  // The spotlighted thing stays usable (an info step may be answered while it explains)
  // — except the pointer's Guide/menu button, which would start something underneath.
  useEffect(() => {
    const allowed = (t: EventTarget | null) =>
      t instanceof Node && (!!cardRef.current?.contains(t) || (!!elRef.current?.contains(t) && (!!action || targetsInteractive(key))));
    const guard = (e: Event) => { if (!allowed(e.target)) { e.preventDefault(); e.stopPropagation(); } };
    const types = ['click', 'mousedown', 'pointerdown', 'dblclick', 'contextmenu'] as const;
    types.forEach(t => document.addEventListener(t, guard, true));
    return () => types.forEach(t => document.removeEventListener(t, guard, true));
  }, [action, key]);

  return (
    <div className="page-guide fixed inset-0 z-[200] pointer-events-none" data-guide-layer="">
      {spot && ring ? (
        <>
          <div aria-hidden="true" className="fixed rounded-xl" style={{
            left: spot.left, top: spot.top, width: spot.width, height: spot.height, boxShadow: `0 0 0 9999px ${DIM}`,
            transition: reduced ? undefined : 'left 200ms ease, top 200ms ease, width 200ms ease, height 200ms ease',
          }} />
          {/* The brand ring: a gradient box masked down to its 2px border. */}
          <div aria-hidden="true" className="page-guide-ring fixed rounded-[14px]" style={{
            left: ring.left, top: ring.top, width: ring.width, height: ring.height, padding: 2, background: RING,
            WebkitMask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)', WebkitMaskComposite: 'xor',
            mask: 'linear-gradient(#000 0 0) content-box exclude, linear-gradient(#000 0 0)',
            transition: reduced ? undefined : 'left 200ms ease, top 200ms ease, width 200ms ease, height 200ms ease',
          }} />
        </>
      ) : <div aria-hidden="true" className="fixed inset-0" style={{ background: DIM }} />}
      <div
        ref={cardRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={bodyId}
        tabIndex={-1}
        className={`fixed pointer-events-auto rounded-2xl border border-border bg-card text-foreground shadow-2xl p-4 focus:outline-none ${phone ? 'left-3 right-3' : 'w-[300px]'}`}
        style={phone
          ? dock === 'top'
            ? { top: 'calc(12px + env(safe-area-inset-top, 0px))' }
            : { bottom: 'calc(12px + env(safe-area-inset-bottom, 0px))' }
          : pos ? { left: pos.left, top: pos.top } : { left: -9999, top: 0 }}
      >
        {progress && <div className="flex items-center gap-2 text-[11px] text-muted-foreground">{progress}</div>}
        <h2 id={titleId} className="mt-1.5 text-sm font-semibold">{title}</h2>
        {/* ⚠️ The note replaces the body only once the target is KNOWN missing — the
            page has set the step's scene up before this mounts, and the target is
            measured before the first paint. */}
        <p id={bodyId} className="mt-1 text-sm text-muted-foreground leading-relaxed">{!target && missingBody ? missingBody : body}</p>
        {list}
        {!list && !(!target && missingBody) && items && items.length > 0 && (
          <ul className="mt-2 space-y-1.5">
            {items.map(it => (
              <li key={it.label} className="flex gap-2 text-sm leading-snug">
                <span aria-hidden="true" className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-muted-foreground/70" />
                <span className="min-w-0"><span className="font-semibold text-foreground">{it.label}</span> <span className="text-muted-foreground">— {it.text}</span></span>
              </li>
            ))}
          </ul>
        )}
        <div className="mt-3 flex flex-wrap items-center gap-2">{footer}</div>
      </div>
    </div>
  );
}

/**
 * On an info step the spotlighted area stays usable (the person may answer while the
 * guide explains) — except the closing pointer's Guide/menu button, which would start
 * something new underneath it.
 */
function targetsInteractive(key: string) {
  return !REPLAY_TARGETS.some(t => key === t || key.startsWith(`${t}|`));
}
